import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Hidden,
  Chip,
  Drawer,
  Divider,
  Icon,
  Menu as MenuUI,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Menu, AccountCircle, Announcement } from '@material-ui/icons';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { isArray, isNull } from 'lodash';
import PropTypes from 'prop-types';

import SCAPlus from '~/assets/images/logo.png';
import TeoIcon from '~/assets/images/teo.png';
import { Spinner, Navigation, BadgeAlert, Modal } from '~/components';
import { MODULOTEO } from '~/constants/modules';
import { t } from '~/helpers';
import api from '~/services/api';
import {
  signOut,
  signLogOut,
  signInRequest,
  signInMicrosoft,
  signInRequestRefressToken,
} from '~/store/modules/auth/actions';
import {
  requestNavigation,
  toggleDrawer,
} from '~/store/modules/drawer/actions';
import { teoPresentationViewed } from '~/store/modules/user/actions';

import {
  AssistantChat,
  ChangePassword,
  HelpButton,
  TeoOnboarding,
} from './components';
import { Container, NavBar, Navi, Logo, Main, Avatar } from './styles';

const TEO_ONBOARDING_TRANSLATION_KEYS = [
  's.assistenteteo.apresentacao.tituloframe1',
  's.assistenteteo.apresentacao.mensagemframe1',
  's.assistenteteo.apresentacao.tituloframe2',
  's.assistenteteo.apresentacao.mensagemframe2',
  's.assistenteteo.apresentacao.proximo',
  's.assistenteteo.apresentacao.pular',
  's.assistenteteo.apresentacao.entendi',
];

const useStyles = makeStyles(() => ({
  drawerPaper: {
    width: '280px',
    marginTop: 0,
    justifyContent: 'flex-start',
  },
  rightDrawerPaper: {
    width: '420px',
    maxWidth: '100vw',
    height: '100%',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
}));

function DefaultLayout({ children }) {
  const changePasswordRef = useRef(null);
  const teoButtonRef = useRef(null);
  const teoCompletionPromiseRef = useRef(null);
  const location = useLocation();

  const { title, navigations, loading, opened } = useSelector(
    (state) => state.drawer
  );

  const { translate } = useSelector((state) => state.language);
  const { profile } = useSelector((state) => state.user);
  const notificationOpened = useSelector((state) => state.notification.opened);

  const { usuariotememail } = useSelector((state) => state.user.configs);

  const { token } = useSelector((state) => state.auth);

  const modules = useSelector((state) => state.user.modules);
  const moduloTEO = modules.includes(MODULOTEO);

  const classes = useStyles();
  const dispatch = useDispatch();

  const [width, setWidth] = useState(() => window.innerWidth);
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [teoOnboardingStep, setTeoOnboardingStep] = useState(0);
  const [password, setPassword] = useState(false);
  const [displayAtualizacaoSistema, setDisplayAtualizacaoSistema] = useState(
    'none'
  );
  const [informacaoSistema, setInformacaoSistema] = useState(null);

  const websiteJSON = JSON.parse(sessionStorage.getItem('web'));
  const website = useMemo(
    () => ({
      logo: SCAPlus,
      site_name: websiteJSON ? websiteJSON.site_name : 'SCA+ Unidata',
    }),
    [websiteJSON]
  );



  async function handleToggleDrawer() {
    await dispatch(toggleDrawer(!opened));
  }

  async function handleFormPassword() {
    changePasswordRef.current.handleOpenModal();
    setActions(null);
  }

  const releaseModalOpen =
    process.env.REACT_APP_TAG !== profile.versao && notificationOpened;

  const showTeoOnboarding =
    location.pathname === '/dashboardvisaogeral' &&
    moduloTEO === true &&
    !!profile.id &&
    profile.apresentacaoteovisualizada === false &&
    !chatDrawerOpen &&
    !releaseModalOpen;

  const markTeoPresentationAsViewed = useCallback(() => {
    if (teoCompletionPromiseRef.current) {
      return teoCompletionPromiseRef.current;
    }

    const request = api
      .put('assistenteteo/apresentacao/visualizada')
      .then(() => {
        dispatch(teoPresentationViewed());
        return true;
      })
      .catch((err) => {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
        teoCompletionPromiseRef.current = null;
        return false;
      });

    teoCompletionPromiseRef.current = request;
    return request;
  }, [dispatch]);

  async function handleAssistantChat() {
    if (usuariotememail === 'N') {
      toast.error('Ação não permitida para usuários sem e-mail cadastrado.');
      return;
    }

    if (teoOnboardingStep === 2) {
      await markTeoPresentationAsViewed();
    }

    setChatDrawerOpen(true);
  }

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleVerificaBuild = useCallback((infoSistema) => {
    if (isNull(infoSistema)) return;

    const buildReact = `${process.env.REACT_APP_TAG}`;
    const buildSistema = infoSistema.build;

    if (buildSistema === '${buildVersion}') return;

    if (buildReact !== buildSistema) {
      setDisplayAtualizacaoSistema('inline-flex');
    } else {
      setDisplayAtualizacaoSistema('none');
    }
  }, []);

  const handleGetInformacaoSistema = useCallback(async () => {
    try {
      const { data } = await api.get('info');
      const buildReact = `${process.env.REACT_APP_TAG}`;
      const buildSistema = data.build;

      setInformacaoSistema(data);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  // Implements refresh token
  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const renewToken = async () => {
      try {
        const { data } = await api.post('logins/refresh', {
          token,
        });

        api.defaults.headers.token = data;
        api.defaults.headers.Authorization = `Bearer ${data}`;
        api.defaults.headers.common.Authorization = `Bearer ${data}`;
        dispatch(signInRequestRefressToken(data));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    };

    const isTime = setInterval(() => {
      renewToken();
    }, 120000);

    return () => clearInterval(isTime);
  }, [dispatch, token]);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const isTime = setInterval(() => {
      handleGetInformacaoSistema();
      handleVerificaBuild(informacaoSistema);
    }, 60000);

    return () => clearInterval(isTime);
  }, [informacaoSistema]);

  useEffect(() => {
    const defNavigation = navigations.map((navi) => {
      if (navi.submenus.length === 0) {
        return {
          key: navi.idmenu,
          title: t(navi.codigolabelsistema),
          url: navi.rotamenu,
          icon: <Icon>{navi.iconemenu || 'label'}</Icon>,
        };
      }

      return {
        key: navi.idmenu,
        title: t(navi.codigolabelsistema),
        icon: <Icon>{navi.iconemenu || 'label'}</Icon>,
        children: navi.submenus.map((one) => {
          if (one.submenus.length === 0) {
            return {
              key: one.idmenu,
              title: t(one.codigolabelsistema),
              url: one.rotamenu,
            };
          }

          return {
            key: one.idmenu,
            title: t(one.codigolabelsistema),
            children: one.submenus.map((two) => ({
              key: two.idmenu,
              title: t(two.codigolabelsistema),
              url: two.rotamenu,
            })),
          };
        }),
      };
    });

    setItems(defNavigation);
  }, [navigations, translate]);

  useEffect(() => {
    dispatch(requestNavigation());
  }, [dispatch]);

  return (
    <Container open={opened}>
      <CssBaseline />

      <AppBar position="fixed">
        <Toolbar>
          <IconButton color="inherit" onClick={() => handleToggleDrawer()}>
            <Menu />
          </IconButton>
          <Typography variant="h6" noWrap style={{ width: '100%' }}>
            {title}
          </Typography>
          <Chip
            icon={<Announcement />}
            color="secondary"
            onClick={() => handleRefresh()}
            label={t('s.geral.alertasistema.atualizacaodisponivel')}
            style={{
              marginRight: '2em',
              paddingLeft: '1em',
              display: displayAtualizacaoSistema,
            }}
          />

          {moduloTEO === true && (
            <Tooltip title="Assistente virtual">
              <IconButton
                ref={teoButtonRef}
                color="inherit"
                aria-label="Abrir assistente virtual"
                onClick={handleAssistantChat}
                style={{ width: 48, height: 48, padding: 6 }}
              >
                <img
                  src={TeoIcon}
                  alt="Teo"
                  style={{
                    width: 36,
                    height: 36,
                    objectFit: 'contain',
                  }}
                />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={t('s.geral.helpbutton')}>
            <span>
              <HelpButton pathname={window.location.pathname} />
            </span>
          </Tooltip>

          <Tooltip title={t('s.geral.infochangelog')}>
            <IconButton
              color="inherit"
              onClick={() => window.open('/changesrelease', '_blank')}
            >
              <ErrorOutlineIcon />
            </IconButton>
          </Tooltip>

          <BadgeAlert />
          <Tooltip title={t('s.menu.cadastro.usuario')}>
            <IconButton
              color="inherit"
              edge="end"
              onClick={(e) => setActions(e.currentTarget)}
            >
              <Avatar>
                <AccountCircle />
                <p>
                  {profile.name}
                  <small>{profile.nomeperfil}</small>
                </p>
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <NavBar open={opened}>
        <Spinner loading={loading} />

        <Hidden smUp implementation="css">
          {width <= 768 && (
            <Drawer
              variant="temporary"
              anchor="left"
              open={opened}
              onClose={() => dispatch(toggleDrawer(false))}
              classes={{
                paper: classes.drawerPaper,
                margin: 0,
              }}
              ModalProps={{ keepMounted: true }}
            >
              <Navi open={opened}>
                <Logo open={opened}>
                  <img
                    src={
                      websiteJSON && websiteJSON.logo_mobile
                        ? websiteJSON.logo_mobile
                        : website.logo
                    }
                    alt={
                      websiteJSON && websiteJSON.site_name
                        ? websiteJSON.site_name
                        : website.site_name
                    }
                  />
                </Logo>
                <Divider />
                <Navigation lists={items} />
              </Navi>
            </Drawer>
          )}
        </Hidden>

        <Hidden xsDown implementation="css">
          {width > 768 && (
            <Drawer
              variant="permanent"
              anchor="left"
              className="drawer-desktop"
            >
              <Navi open={opened}>
                <Logo open={opened}>
                  <img
                    src={
                      websiteJSON && websiteJSON.logo
                        ? websiteJSON.logo
                        : website.logo
                    }
                    alt={
                      websiteJSON && websiteJSON.site_name
                        ? websiteJSON.site_name
                        : website.site_name
                    }
                  />
                </Logo>
                <Divider />
                <Navigation lists={items} />
              </Navi>
            </Drawer>
          )}
        </Hidden>
      </NavBar>

      {moduloTEO === true && usuariotememail === 'S' && (
        < Drawer
          anchor="right"
          open={chatDrawerOpen}
          onClose={() => setChatDrawerOpen(false)}
          classes={{ paper: classes.rightDrawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          <AssistantChat
            open={chatDrawerOpen}
            onClose={() => setChatDrawerOpen(false)}
          />
        </Drawer>
      )}

      <Main open={opened}>{children}</Main>

      {showTeoOnboarding && (
        <TeoOnboarding
          targetRef={teoButtonRef}
          layoutKey={opened}
          onComplete={markTeoPresentationAsViewed}
          onStepChange={setTeoOnboardingStep}
        />
      )}

      <MenuUI
        anchorEl={actions}
        keepMounted
        open={!!actions}
        onClose={() => setActions(null)}
      >
        <MenuItem onClick={() => handleFormPassword()}>
          {t('s.geral.alterarsenha')}
        </MenuItem>

        {/* <MenuItem onClick={() => dispatch(signOut())}>Sair</MenuItem> */}

        <MenuItem onClick={() => dispatch(signLogOut(token))}>Sair</MenuItem>
      </MenuUI>

      <Modal
        disableBackdropClick
        form
        ref={changePasswordRef}
        maxWidth="xs"
        title={t('s.geral.alterarsenha')}
        component={ChangePassword}
      />
    </Container >
  );
}

DefaultLayout.propTypes = {
  children: PropTypes.element.isRequired,
};

export default DefaultLayout;
